import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import { ReportStatusCellComponent } from './report-status-cell/report-status-cell.component';
import {
  DataService,
  SecurityService,
  SystemService,
  Utilities,
  WorkflowContextService
} from 'src/app/services';
import { WorkflowType } from 'src/app/models';
import {
  FilterBuilderFilter,
  FilterBuilderOutput,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import {
  DailyFeeReportRequest,
  PagedDailyFeeReportRequest,
  PagedDailyFeeReportResponse
} from 'src/app/models/financial-reports';
import { ApplicationStatusPipe } from 'src/app/pipes/application-status.pipe';
import { MatSort } from '@angular/material/sort';
import { ToastrService } from 'ngx-toastr';
import { ComponentIds } from 'src/app/models/component-ids';
import { ColumnOption } from 'src/app/models/grid-settings';
import { ActivatedRoute, Router } from '@angular/router';
import { DataTableHelpers } from 'src/app/components/system/datatable/datatable-helper';
import { ClickableCellComponent } from 'src/app/components/common/common-table/clickable-cell/clickable-cell.component';

@Component({
  selector: 'app-financial-fees',
  templateUrl: './financial-fees.component.html',
  styleUrls: ['./financial-fees.component.css'],
  standalone: false
})
export class FinancialFeesComponent implements OnInit, OnChanges {
  loading = true;
  id = ComponentIds.FEES;
  title = 'Fees';
  errorMessage = '';
  report: PagedDailyFeeReportResponse;
  defaultFilters: FilterBuilderFilter[];
  filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];
  // isEditorVisible: boolean = false;

  timeFrame: FilterBuilderParam = {
    name: 'Paid Date',
    id: 'timeframe',
    inputType: 'date',
    types: ['range']
  };

  params: FilterBuilderParam[] = [
    {
      name: 'Permit #',
      types: ['is', 'contains'],
      id: 'permitNumber'
    },
    {
      name: 'Amount',
      id: 'amount',
      types: ['is'],
      inputType: 'number'
    },
    this.timeFrame,
    {
      name: 'Recipient',
      id: 'recipientName'
    },
    {
      name: 'App Type',
      id: 'applicationName',
      options: []
    },
    {
      name: 'Payer',
      id: 'payer'
    },
    {
      name: 'Status',
      id: 'status',
      types: ['is', 'in'],
      options: [
        {
          name: 'Not Started',
          value: '0'
        },
        {
          name: 'In Progress',
          value: '1'
        },
        {
          name: 'Completed',
          value: '2'
        },
        {
          name: 'Voided',
          value: '3'
        },
        {
          name: 'Rejected',
          value: '4'
        },
        {
          name: 'Discarded',
          value: '5'
        }
      ]
    }
  ];

  filters: FilterBuilderOutput = {};
  public useExcelFormat: boolean;

  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  exportColumns: string[] = [];
  rows: any[];
  public page = {
    size: 10,
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0
  };
  @Input() public sortField = 'paymentDate';
  @Input() public sortDescending = false;

  lastQuery = {};

  // column prop strings to be hidden on the grid by default
  defaultHiddenColumns: string[] = ['appDescription', 'clientName'];
  // column prop strings that should have no option to hide in the grid, and are not exportable (such as Actions button)
  actionColumns: string[] = [];
  // column prop strings to be excluded from exports by default
  defaultExportExludedColumns: string[] = [];

  @ViewChild(MatSort) sort!: MatSort;
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-report-fees-janus';
  constructor(
    private systemSvc: SystemService,
    private dataSvc: DataService,
    private securitySvc: SecurityService,
    private toast: ToastrService,
    private _context: WorkflowContextService,
    private appStatusPipe: ApplicationStatusPipe,
    private _dtHelpers: DataTableHelpers,
    public router: Router,
    public route: ActivatedRoute
  ) {}

  ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.page.size = pageSize;
      }
    }
    this.defaultFilters = [
      new FilterBuilderFilter({
        type: 'range',
        value: 'this_1_months',
        queryType: 'range',
        param: this.timeFrame,
        paramId: this.timeFrame.id
      })
    ];

    this.getApplications();
    this.getReport();

    this.columns = [
      {
        key: 'permitId',
        label: 'Permit #',
        headerType: 'text',
        cellType: 'component',
        showExportOption: true,
        component: ClickableCellComponent,
        componentInputs: row => ({
          text: row.permitId,
          row: row
        }),
        componentOutputs: {
          clicked: rowData => this.onNameClick(rowData)
        }
      },
      {
        key: 'amount',
        label: 'Amount',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'paymentDate',
        label: 'Paid',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'recipientName',
        label: 'Recipient',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'applicationName',
        label: 'App Type',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'appStatus',
        label: 'Status',
        headerType: 'text',
        cellType: 'component',
        component: ReportStatusCellComponent,
        componentInputs: row => ({
          status: row.appStatus
        }),
        showExportOption: true
      },
      {
        key: 'appDescription',
        label: 'Application Description',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      },
      {
        key: 'clientName',
        label: 'Jurisdiction',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      }
    ];

    this.columnOptions = this.columns.map(c => ({
      key: c.key,
      name: c.key,
      label: c.label,
      checked: !c.hidden,
      includeInExport: c.showExportOption ?? true
    }));

    this.setAvailableColumns();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  isEquivalent(a: any, b: any) {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);

    if (aProps.length !== bProps.length) {
      return false;
    }

    for (let i = 0; i < aProps.length; i++) {
      const propName = aProps[i];
      if (a[propName] !== b[propName]) {
        return false;
      }
    }
    return true;
  }

  getApplications() {
    this.dataSvc
      .getPublishedWorkflows(this._context.client, [
        WorkflowType.ContractorRegistration,
        WorkflowType.ContractorRenewal,
        WorkflowType.Other,
        WorkflowType.Permit,
        WorkflowType.Renewable,
        WorkflowType.MobileInspection
      ])
      .subscribe(workflows => {
        const param = this.params.find(p => p.id === 'applicationName');
        param.options = workflows
          .map(w => ({
            name: w.version.name,
            value: w.id
          }))
          .sort((a: any, b: any) => a.name - b.name);
      });
  }

  private getQuery(): DailyFeeReportRequest {
    const filters = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }

    const query = {
      clientId: this._context.client.id,
      filters,
      useExcelFormat: this.useExcelFormat,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      exportColumns: this.exportColumns
    };

    return query;
  }

  private getPagedQuery(): PagedDailyFeeReportRequest {
    const filters = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }

    const query = {
      clientId: this._context.client.id,
      filters,
      pageNumber: this.page.pageNumber,
      pageSize: this.page.size,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };

    return query;
  }

  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (event.sortField === null) {
      // reset to default sorting
      this.sortField = 'paymentDate'; // choose your default
      this.sortDescending = false;
    } else {
      this.sortField = event.sortField;
      this.sortDescending = event.sortDescending ?? false;
    }
    this.page.pageNumber = 0;
    this.getReport();
  }

  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.page.size = pageSize;
    this.page.pageNumber = 0;
    this.getReport();
  }

  onPageChange(page: number) {
    this.page.pageNumber = page;
    this.getReport();
  }

  onRetry() {
    this.getReport();
  }

  getReport() {
    const query = this.getPagedQuery();
    if (!this.isEquivalent(query, this.lastQuery)) {
      this.lastQuery = { ...query };
      this.loading = true;
      this.systemSvc.getPagedDailyFeeReport(query).subscribe({
        next: report => {
          // this.page.size = report.pageCount;
          this.page.totalElements = report.recordCount;
          this.page.totalPages = report.pageCount;
          this.rows = report.items.map(i => {
            i['permitId'] = i.permitId;
            i['amount'] = Utilities.formatCurrency(i.amount);
            i['paymentDate'] = i.paymentDate;
            i['recipientName'] = i.recipientName;
            i['applicationName'] = i.applicationName;
            i['username'] = i.username;
            i['appStatus'] = this.appStatusPipe.transform(i.appStatus);
            i['appDescription'] = i.applicationDescription;
            i['clientName'] = i.clientName;
            return i;
          });
          this.report = report;
          this.loading = false;
        },
        error: err => {
          console.error(err);
          this.loading = false;
          this.errorMessage = 'Failed to load report. Please try again.';
        }
      });
    }
  }

  onExportColumnsChange(cols: string[]) {
    this.exportColumns = cols;
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const query = this.getQuery();
    this.systemSvc.exportDailyFeeReport(query).subscribe(
      data => {
        if (this.useExcelFormat) {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `fee-report-export-${new Date().getTime()}.xlsx`;
          link.click();
        } else {
          const blob = new Blob([data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `fee-report-export-${new Date().getTime()}.csv`;
          link.click();
        }
      },
      err => {
        this.toast.error('Problem while downloading the file.');
        console.error(err);
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getReport();
  }

  onFiltersChanged(filters: FilterBuilderOutput) {
    this.filters = filters;
    this.page.pageNumber = 0; // reset to first page
    this.getReport();
  }

  onColumnsChanged(columnSet: { columns: any[]; exportColumns: any[] }) {
    // Update visible columns
    const visibleKeys = columnSet.columns.map(c => c.name);

    this.columns = this.availableColumns.map(col => ({
      ...col,
      hidden: !visibleKeys.includes(col.key)
    }));

    // Update export columns
    this.exportColumns = columnSet.exportColumns.map(c => c.name);
  }

  // added by Vipul on 16-07-2026
  onNameClick(row: any) {
    const segments = this.router.routerState.snapshot.url
      .split('/')
      .slice(0, -3);
    this.router.navigate([...segments, 'workflow-applications', 'data'], {
      queryParams: { id: row.applicationId }
    });
  }
  // end by Vipul on 16-07-2026
}
